import { useCallback, useEffect, useMemo, useState } from 'react';
import { writeXlsx } from '../../reports/engine';
import { auditService } from '../../services/auditService';
import {
  downloadFile,
  fmtMoney,
  fmtQty,
  safeFileName,
  todayStamp,
  XLSX_TYPE,
} from '../Reports/ui';
import DataGrid, { dash, numberColumn } from '../../components/DataGrid';
import BookStockUploader from './BookStockUploader';
import './ComparisonPanel.css';
import { PageLoader, useBusyLoader } from '../../components/Loader';

const MODES = [
  { id: 'book', label: 'Scanned vs book stock' },
  { id: 'master', label: 'Scanned vs master list' },
  { id: 'people', label: 'Team & day' },
  { id: 'scans', label: 'Valid & invalid scans' },
];

const MAX_MATRIX_COLUMNS = 12;

const BOOK_FILTERS = [
  { id: 'diff', label: 'Items with a difference', test: (r) => r.variance !== 0 },
  { id: 'shortage', label: 'Shortage', test: (r) => r.status === 'shortage' },
  { id: 'excess', label: 'Excess (in book)', test: (r) => r.status === 'excess' },
  { id: 'not_scanned', label: 'In book, not scanned', test: (r) => r.in_book && r.scanned_qty === 0 },
  { id: 'not_in_book', label: 'Scanned, not in book', test: (r) => r.status === 'not_in_book' },
  { id: 'matched', label: 'Matched', test: (r) => r.status === 'matched' },
  { id: 'all', label: 'All items', test: () => true },
];

const MASTER_FILTERS = [
  { id: 'not_scanned', label: 'In master, not scanned', test: (r) => r.status === 'not_scanned' },
  { id: 'not_in_master', label: 'Scanned, not in master', test: (r) => r.status === 'not_in_master' },
  { id: 'scanned', label: 'Scanned and in master', test: (r) => r.status === 'scanned' },
  { id: 'all', label: 'All items', test: () => true },
];

const STATUS_LABEL = {
  shortage: 'Shortage',
  excess: 'Excess',
  matched: 'Matched',
  not_in_book: 'Not in book',
  not_scanned: 'Not scanned',
  not_in_master: 'Not in master',
  scanned: 'Scanned',
};

const sum = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);

// ---- Grid columns shared by the comparisons ---------------------------------

const EFFECT_SIGN = { add: ' (+)', subtract: ' (−)', none: ' (note)' };
const EFFECT_TIP = {
  add: 'Manual adjustment: added to the counted quantity',
  subtract: 'Manual adjustment: subtracted from the counted quantity',
  none: 'Manual adjustment shown only: does not change the variance',
};
const qtyCol = (headerName, field, options) => numberColumn(headerName, field, fmtQty, options);
const moneyCol = (headerName, field, options) => numberColumn(headerName, field, fmtMoney, options);

/** Variance-style number: red below zero, amber above. */
const signedCol = (headerName, field, options = {}) => ({
  ...qtyCol(headerName, field, options),
  cellClass: (p) => (p.value < 0 ? 'data-grid__neg' : p.value > 0 ? 'data-grid__pos' : ''),
});

function statusCol(field = 'status') {
  return {
    headerName: 'Status',
    field,
    minWidth: 130,
    valueGetter: (p) => STATUS_LABEL[p.data[field]] || p.data[field],
    cellRenderer: (p) => (
      <span className={`compare-pill compare-pill--${p.data[field]}`}>{STATUS_LABEL[p.data[field]]}</span>
    ),
  };
}

/** EAN (or article counted as a whole). */
const eanCol = {
  headerName: 'EAN',
  field: 'ean',
  minWidth: 170,
  pinned: 'left',
  cellRenderer: (p) => (
    <>
      {p.value}
      {p.data.by_article ? <ArticleTag /> : null}
    </>
  ),
};

function Card({ label, value, note, tone }) {
  return (
    <article className={`compare-card${tone ? ` compare-card--${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <p>{note}</p> : null}
    </article>
  );
}

/** Filter, extra controls and Excel button, shown in the grid's toolbar. */
function Toolbar({ filter, setFilter, filters, onExport, exporting, children }) {
  return (
    <>
      {filters ? (
        <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Show">
          {filters.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}
      {children}
      <button type="button" className="compare-button compare-button--ghost" onClick={onExport} disabled={exporting}>
        {exporting ? 'Preparing…' : 'Download Excel'}
      </button>
    </>
  );
}

async function exportWorkbook(fileName, sheets) {
  const bytes = await writeXlsx(sheets);
  downloadFile(fileName, bytes, XLSX_TYPE);
}

const SCAN_LOCATION_NOTE =
  'Like the phone report (Valid_Scans / Invalid_Scans), with time and phone. Scans sent by older app versions have no location.';

/** Marks an item scanned by article number and counted as a whole article. */
export function ArticleTag() {
  return (
    <small
      className="compare-muted"
      title="Scanned by article number: compared against the book stock of all the article's EANs together"
    >
      {' '}· article (all EANs)
    </small>
  );
}

function scanTime(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

/**
 * Scan status for reports:
 *   valid             EAN in the product list when scanned
 *   resolved          scanned invalid, corrected by the store team (green)
 *   invalid           still invalid (red)
 */
export function scanStatus(row) {
  if (row.original_ean) return 'resolved';
  return row.is_valid ? 'valid' : 'invalid';
}

export const SCAN_STATUS_LABEL = {
  valid: 'Valid',
  resolved: 'Invalid resolved',
  invalid: 'Invalid',
};

/** Who resolved an invalid scan, and when. */
function resolvedBy(row) {
  if (!row.original_ean) return '';
  return [row.corrected_by_name, row.corrected_at ? scanTime(row.corrected_at) : '']
    .filter(Boolean)
    .join(' · ');
}

/** Status pill: green "Invalid resolved" (with who/when), red "Invalid", green "Valid". */
export function ScanStatusPill({ row }) {
  const status = scanStatus(row);
  if (status === 'resolved') {
    return (
      <span className="scan-status">
        <span className="compare-pill compare-pill--resolved">Invalid resolved</span>
        <small className="scan-status__by">
          {resolvedBy(row) || 'Resolved'} · was {row.original_ean}
        </small>
      </span>
    );
  }
  return (
    <span className={`compare-pill compare-pill--${status === 'valid' ? 'scanned' : 'invalid'}`}>
      {SCAN_STATUS_LABEL[status]}
    </span>
  );
}

/** "Valid Scans", "Invalid Resolved" and "Invalid Scans" sheets (phone report style). */
function scanSheets(auditName, scansData) {
  const rows = scansData?.rows || [];
  const columns = [
    { label: 'Location', key: 'location', type: 'text', width: 16 },
    { label: 'EAN', key: 'ean', type: 'text', width: 16 },
    { label: 'Username', key: 'scanned_by', type: 'text', width: 20 },
    { label: 'Description', key: 'product_name', type: 'text', width: 34 },
    { label: 'Scan Quantity', key: 'qty', type: 'qty' },
    { label: 'Is Valid', key: 'is_valid', type: 'text', value: (r) => (r.is_valid ? 'Yes' : 'No') },
    { label: 'Status', key: 'status', type: 'text', width: 16, value: (r) => SCAN_STATUS_LABEL[scanStatus(r)] },
    { label: 'Scanned At', key: 'scanned_at', type: 'text', width: 22, value: (r) => scanTime(r.scanned_at) },
    { label: 'Device', key: 'device_id', type: 'text', width: 18 },
    { label: 'Corrected From', key: 'original_ean', type: 'text', width: 16 },
    { label: 'Resolved By', key: 'corrected_by_name', type: 'text', width: 20 },
    { label: 'Resolved At', key: 'corrected_at', type: 'text', width: 22, value: (r) => (r.corrected_at ? scanTime(r.corrected_at) : '') },
    { label: 'Scanned As (Article)', key: 'scanned_code', type: 'text', width: 16 },
  ];
  return [
    {
      name: 'Valid Scans',
      title: [`${auditName} — Valid scans`],
      notes: [SCAN_LOCATION_NOTE],
      columns,
      rows: rows.filter((r) => r.is_valid),
    },
    {
      name: 'Invalid Resolved',
      title: [`${auditName} — Invalid scans resolved by the store team`],
      notes: ['Scanned with an EAN not in the product list, then corrected; counted as valid.'],
      columns,
      rows: rows.filter((r) => scanStatus(r) === 'resolved'),
    },
    {
      name: 'Invalid Scans',
      title: [`${auditName} — Invalid scans (EAN not in the product list)`],
      notes: [SCAN_LOCATION_NOTE],
      columns,
      rows: rows.filter((r) => !r.is_valid),
    },
  ];
}

/** Loads every scan with its status for the Excel downloads. */
async function loadScanSheets(auditId, auditName) {
  const scansData = await auditService.getComparison(auditId, 'scans');
  return scanSheets(auditName, scansData);
}

/** Invalid scans are not part of the quantities above; this says how many there are. */
function InvalidNotice({ invalid, onShowScans }) {
  if (!invalid) return null;
  return (
    <div className={`compare-invalid${invalid.scans ? '' : ' compare-invalid--none'}`}>
      <span>
        {invalid.scans
          ? `${fmtQty(invalid.scans)} invalid scans (qty ${fmtQty(invalid.qty)}, ${fmtQty(invalid.eans)} EANs not in the product list) — not counted in the figures above until they are corrected on the Valid EAN tab.`
          : 'No invalid scans synced.'}
      </span>
      {invalid.scans && onShowScans ? (
        <button type="button" className="compare-link" onClick={onShowScans}>
          See valid &amp; invalid scans
        </button>
      ) : null}
    </div>
  );
}

// ---- Scanned vs book stock ------------------------------------------------

function BookComparison({ auditId, auditName, data, canManage, onChanged, onShowScans }) {
  const [filter, setFilter] = useState('diff');
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useBusyLoader('Preparing Excel file…');
  const items = data?.items || [];
  const book = data?.book;
  // Manual-adjustment tabs that have quantities on this schedule's items.
  const adjCats = useMemo(() => {
    const used = new Set();
    items.forEach((r) => Object.keys(r.adjustments || {}).forEach((code) => used.add(code)));
    return (data?.adjustment_categories || []).filter((c) => used.has(c.code));
  }, [items, data]);
  const hasAdj = adjCats.length > 0;

  const totals = useMemo(() => {
    const inBook = items.filter((r) => r.in_book);
    return {
      bookItems: inBook.length,
      bookQty: sum(inBook, 'book_qty'),
      scannedQty: sum(items, 'scanned_qty'),
      adjustmentQty: sum(items, 'adjustment_qty'),
      countedQty: sum(items, 'counted_qty'),
      shortageQty: sum(items, 'shortage_qty'),
      excessQty: sum(items, 'excess_qty'),
      shortageMrp: sum(items, 'shortage_mrp'),
      excessMrp: sum(items, 'excess_mrp'),
      shortageCost: sum(items, 'shortage_cost'),
      excessCost: sum(items, 'excess_cost'),
      notInBook: items.filter((r) => r.status === 'not_in_book').length,
      notScanned: inBook.filter((r) => r.scanned_qty === 0).length,
      missingRate: items.filter((r) => r.variance !== 0 && r.in_book && (r.mrp === null || r.cost === null)).length,
    };
  }, [items]);

  const shown = useMemo(() => {
    const test = (BOOK_FILTERS.find((f) => f.id === filter) || BOOK_FILTERS[0]).test;
    return items.filter(test);
  }, [items, filter]);
  const bookColumns = useMemo(
    () => [
      eanCol,
      {
        headerName: 'Description',
        field: 'description',
        minWidth: 220,
        flex: 2,
        valueFormatter: (p) => dash(p.value),
        getQuickFilterText: (p) => `${p.data.description || ''} ${p.data.article || ''} ${p.data.category || ''}`,
        cellRenderer: (p) => (
          <>
            {p.data.description || '—'}
            {p.data.article ? <small className="compare-muted"> · {p.data.article}</small> : null}
          </>
        ),
      },
      qtyCol('Book', 'book_qty'),
      qtyCol('Scanned', 'scanned_qty'),
      ...adjCats.map((c) =>
        qtyCol(`${c.name}${EFFECT_SIGN[c.effect]}`, `adj_${c.code}`, {
          blankZero: true,
          colDef: { valueGetter: (p) => Number(p.data.adjustments?.[c.code] || 0), headerTooltip: EFFECT_TIP[c.effect] },
        }),
      ),
      ...(hasAdj ? [qtyCol('Counted', 'counted_qty', { colDef: { headerTooltip: 'Scanned + manual adjustments' } })] : []),
      signedCol('Variance', 'variance'),
      moneyCol('Shortage MRP', 'shortage_mrp', { colDef: { valueGetter: (p) => (p.data.shortage_qty ? p.data.shortage_mrp : null) } }),
      moneyCol('Excess MRP', 'excess_mrp', { colDef: { valueGetter: (p) => (p.data.excess_qty ? p.data.excess_mrp : null) } }),
      moneyCol('Shortage cost', 'shortage_cost', { colDef: { valueGetter: (p) => (p.data.shortage_qty ? p.data.shortage_cost : null) } }),
      moneyCol('Excess cost', 'excess_cost', { colDef: { valueGetter: (p) => (p.data.excess_qty ? p.data.excess_cost : null) } }),
      statusCol(),
    ],
    [adjCats, hasAdj],
  );

  const removeBook = async () => {
    if (!window.confirm('Remove the book stock from this schedule? The comparison will be empty until a new file is uploaded.')) return;
    await auditService.deleteBookStock(auditId);
    onChanged();
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const columns = [
        { label: 'EAN', key: 'ean', type: 'text', width: 16 },
        { label: 'Counted As', key: 'by_article', type: 'text', value: (r) => (r.by_article ? 'Article (all its EANs)' : 'EAN') },
        { label: 'Article', key: 'article', type: 'text' },
        { label: 'Description', key: 'description', type: 'text', width: 36 },
        { label: 'Category', key: 'category', type: 'text' },
        { label: 'Book Qty', key: 'book_qty', type: 'qty' },
        { label: 'Scanned Qty', key: 'scanned_qty', type: 'qty' },
        ...adjCats.map((c) => ({
          label: `${c.name}${EFFECT_SIGN[c.effect]}`,
          key: `adj_${c.code}`,
          type: 'qty',
          value: (r) => Number(r.adjustments?.[c.code] || 0) || null,
        })),
        ...(hasAdj ? [{ label: 'Counted Qty', key: 'counted_qty', type: 'qty' }] : []),
        { label: 'Variance', key: 'variance', type: 'qty' },
        { label: 'Shortage Qty', key: 'shortage_qty', type: 'qty' },
        { label: 'Excess Qty', key: 'excess_qty', type: 'qty' },
        { label: 'MRP', key: 'mrp', type: 'money' },
        { label: 'Shortage MRP', key: 'shortage_mrp', type: 'money' },
        { label: 'Excess MRP', key: 'excess_mrp', type: 'money' },
        { label: 'Cost', key: 'cost', type: 'money' },
        { label: 'Shortage Cost', key: 'shortage_cost', type: 'money' },
        { label: 'Excess Cost', key: 'excess_cost', type: 'money' },
        { label: 'Status', key: 'status', type: 'text', value: (r) => STATUS_LABEL[r.status] || r.status },
        { label: 'In Master', key: 'in_master', type: 'text', value: (r) => (r.in_master ? 'Yes' : 'No') },
      ];
      const title = (label) => [`${auditName} — ${label}`];
      await exportWorkbook(`${safeFileName(auditName)} - scanned vs book ${todayStamp()}.xlsx`, [
        {
          name: 'Summary',
          title: title('Scanned vs book stock'),
          notes: [
            `Book stock file: ${book?.original_filename || '—'}; generated ${new Date().toLocaleString()}.`,
            'Values are blank where the rate is missing in the book file (never treated as zero).',
          ],
          columns: [
            { label: 'Item', key: 'item', type: 'text', width: 34 },
            { label: 'Quantity', key: 'qty', type: 'qty' },
            { label: 'MRP Value', key: 'mrp', type: 'money' },
            { label: 'Cost Value', key: 'cost', type: 'money' },
          ],
          rows: [
            { item: 'Items in book', qty: totals.bookItems },
            { item: 'Book quantity', qty: totals.bookQty },
            { item: 'Scanned quantity', qty: totals.scannedQty },
            ...(hasAdj
              ? [
                  ...adjCats.map((c) => ({
                    item: `${c.name}${EFFECT_SIGN[c.effect]}`,
                    qty: items.reduce((t, r) => t + Number(r.adjustments?.[c.code] || 0), 0),
                  })),
                  { item: 'Counted quantity (scanned + adjustments)', qty: totals.countedQty },
                ]
              : []),
            { item: 'Shortage', qty: totals.shortageQty, mrp: totals.shortageMrp, cost: totals.shortageCost },
            { item: 'Excess', qty: totals.excessQty, mrp: totals.excessMrp, cost: totals.excessCost },
            { item: 'Items in book, not scanned', qty: totals.notScanned },
            { item: 'Items scanned, not in book', qty: totals.notInBook },
          ],
        },
        { name: 'All items', title: title('All items'), columns, rows: items },
        { name: 'Differences', title: title('Items with a difference'), columns, rows: items.filter((r) => r.variance !== 0) },
        ...(await loadScanSheets(auditId, auditName)),
      ]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="compare-book-bar">
        <div>
          {book ? (
            <>
              <strong>Book stock: {book.original_filename || 'uploaded file'}</strong>
              <span>
                {fmtQty(book.row_count)} rows · {fmtQty(book.ean_count)} EANs · loaded{' '}
                {new Date(book.uploaded_at).toLocaleString()}
              </span>
            </>
          ) : (
            <>
              <strong>No book stock loaded yet</strong>
              <span>
                {canManage
                  ? 'Upload the client\'s stock file (EAN and book quantity, optionally MRP and cost) to compare it with the scans.'
                  : 'The audit team has not loaded the book stock for this schedule yet.'}
              </span>
            </>
          )}
        </div>
        {canManage && !uploading ? (
          <div className="compare-book-bar__actions">
            <button type="button" className="compare-button" onClick={() => setUploading(true)}>
              {book ? 'Replace book stock…' : 'Upload book stock…'}
            </button>
            {book ? (
              <button type="button" className="compare-button compare-button--ghost" onClick={removeBook}>
                Remove
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {uploading ? (
        <BookStockUploader
          auditId={auditId}
          onCancel={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            onChanged();
          }}
        />
      ) : null}

      {book ? (
        <>
          <section className="compare-cards">
            <Card label="Book quantity" value={fmtQty(totals.bookQty)} note={`${fmtQty(totals.bookItems)} items`} />
            <Card label="Scanned quantity" value={fmtQty(totals.scannedQty)} note={`${fmtQty(totals.notScanned)} book items not scanned`} />
            {hasAdj ? (
              <Card
                label="Manual adjustments"
                value={`${totals.adjustmentQty > 0 ? '+' : ''}${fmtQty(totals.adjustmentQty)}`}
                note={`Counted ${fmtQty(totals.countedQty)} · ${adjCats.map((c) => c.name).join(', ')}`}
              />
            ) : null}
            <Card
              label="Shortage"
              value={fmtQty(totals.shortageQty)}
              note={`MRP ${fmtMoney(totals.shortageMrp)} · Cost ${fmtMoney(totals.shortageCost)}`}
              tone="bad"
            />
            <Card
              label="Excess"
              value={fmtQty(totals.excessQty)}
              note={`MRP ${fmtMoney(totals.excessMrp)} · Cost ${fmtMoney(totals.excessCost)}`}
              tone="warn"
            />
            <Card label="Scanned, not in book" value={fmtQty(totals.notInBook)} note="EANs with no book line" />
          </section>
          <InvalidNotice invalid={data?.invalid} onShowScans={onShowScans} />
          {totals.missingRate ? (
            <p className="compare-muted">
              {fmtQty(totals.missingRate)} items with a difference have no MRP or cost in the book file; their values are
              blank and left out of the totals.
            </p>
          ) : null}

          <DataGrid
            rowData={shown}
            columnDefs={bookColumns}
            getRowId={(row) => row.ean}
            pageSize={50}
            searchPlaceholder="Search EAN, product, article or category…"
            emptyMessage="Nothing to show for this filter."
            toolbar={
              <Toolbar
                filter={filter}
                setFilter={setFilter}
                filters={BOOK_FILTERS}
                onExport={exportExcel}
                exporting={exporting}
              />
            }
          />
        </>
      ) : null}
    </>
  );
}

// ---- Scanned vs master list -------------------------------------------------

function MasterComparison({ auditId, auditName, data, onShowScans }) {
  const [filter, setFilter] = useState('not_scanned');
  const [exporting, setExporting] = useBusyLoader('Preparing Excel file…');
  const items = data?.items || [];

  const counts = useMemo(
    () => ({
      master: items.filter((r) => r.status !== 'not_in_master').length,
      scanned: items.filter((r) => r.status === 'scanned').length,
      notScanned: items.filter((r) => r.status === 'not_scanned').length,
      notInMaster: items.filter((r) => r.status === 'not_in_master').length,
      notInMasterQty: sum(items.filter((r) => r.status === 'not_in_master'), 'scanned_qty'),
      scannedQty: sum(items, 'scanned_qty'),
    }),
    [items],
  );
  const shown = useMemo(() => items.filter((MASTER_FILTERS.find((f) => f.id === filter) || MASTER_FILTERS[0]).test), [items, filter]);
  const masterColumns = useMemo(
    () => [
      eanCol,
      {
        headerName: 'Product',
        field: 'product_name',
        minWidth: 220,
        flex: 2,
        getQuickFilterText: (p) => `${p.data.product_name || ''} ${p.data.internal_code || ''}`,
        cellRenderer: (p) => (
          <>
            {p.data.product_name || '—'}
            {p.data.internal_code ? <small className="compare-muted"> · {p.data.internal_code}</small> : null}
          </>
        ),
      },
      qtyCol('Scanned qty', 'scanned_qty'),
      qtyCol('Scans', 'scan_count'),
      qtyCol('People', 'people'),
      statusCol(),
    ],
    [],
  );
  const coverage = counts.master ? Math.round((counts.scanned / counts.master) * 1000) / 10 : 0;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const columns = [
        { label: 'EAN', key: 'ean', type: 'text', width: 16 },
        { label: 'Counted As', key: 'by_article', type: 'text', value: (r) => (r.by_article ? 'Article (all its EANs)' : 'EAN') },
        { label: 'Product', key: 'product_name', type: 'text', width: 36 },
        { label: 'Internal Code', key: 'internal_code', type: 'text' },
        { label: 'Scanned Qty', key: 'scanned_qty', type: 'qty' },
        { label: 'Scans', key: 'scan_count', type: 'number' },
        { label: 'People', key: 'people', type: 'number' },
        { label: 'Status', key: 'status', type: 'text', value: (r) => STATUS_LABEL[r.status] || r.status },
      ];
      const title = (label) => [`${auditName} — ${label}`];
      await exportWorkbook(`${safeFileName(auditName)} - scanned vs master ${todayStamp()}.xlsx`, [
        { name: 'Not scanned', title: title('Master items not scanned'), columns, rows: items.filter((r) => r.status === 'not_scanned') },
        { name: 'Not in master', title: title('Scanned EANs not in the master'), columns, rows: items.filter((r) => r.status === 'not_in_master') },
        { name: 'All items', title: title('All items'), columns, rows: items },
        ...(await loadScanSheets(auditId, auditName)),
      ]);
    } finally {
      setExporting(false);
    }
  };

  if (!data?.master) {
    return <p className="compare-empty">No master file has been uploaded for this schedule yet.</p>;
  }

  return (
    <>
      <section className="compare-cards">
        <Card label="Master items" value={fmtQty(counts.master)} note={data.master.original_filename || ''} />
        <Card label="Scanned" value={fmtQty(counts.scanned)} note={`${coverage}% of the master`} tone="good" />
        <Card label="Not scanned yet" value={fmtQty(counts.notScanned)} note="In master, no scans" tone="warn" />
        <Card
          label="Not in master"
          value={fmtQty(counts.notInMaster)}
          note={`${fmtQty(counts.notInMasterQty)} qty scanned with unknown EANs`}
          tone="bad"
        />
      </section>
      <InvalidNotice invalid={data?.invalid} onShowScans={onShowScans} />
      <DataGrid
        rowData={shown}
        columnDefs={masterColumns}
        getRowId={(row) => row.ean}
        pageSize={50}
        searchPlaceholder="Search EAN, product or code…"
        emptyMessage="Nothing to show for this filter."
        toolbar={
          <Toolbar
            filter={filter}
            setFilter={setFilter}
            filters={MASTER_FILTERS}
            onExport={exportExcel}
            exporting={exporting}
          />
        }
      />
    </>
  );
}

// ---- Team & day -------------------------------------------------------------

function PeopleComparison({ auditId, auditName, data, onShowScans }) {
  const [by, setBy] = useState('person');
  const [onlyShared, setOnlyShared] = useState(false);
  const [exporting, setExporting] = useBusyLoader('Preparing Excel file…');

  const groups = by === 'person' ? data?.people || [] : data?.days || [];
  const groupKey = by === 'person' ? 'name' : 'day';
  const valuesKey = by === 'person' ? 'by_person' : 'by_day';
  const countKey = by === 'person' ? 'person_count' : 'day_count';
  // Show the biggest groups as columns; the rest are summed into "Others".
  const columns = groups.slice(0, MAX_MATRIX_COLUMNS).map((group) => group[groupKey]);
  const hasOthers = groups.length > MAX_MATRIX_COLUMNS;

  const items = useMemo(() => {
    const list = data?.items || [];
    return onlyShared ? list.filter((item) => item[countKey] > 1) : list;
  }, [data, onlyShared, countKey]);
  const groupColumns = [
    { headerName: by === 'person' ? 'Person' : 'Day', field: groupKey, minWidth: 180 },
    qtyCol('Quantity', 'qty'),
    qtyCol('Items (EANs)', 'items'),
    qtyCol('Scans', 'scans'),
  ];
  const matrixColumns = [
    { headerName: 'EAN', field: 'ean', minWidth: 160, pinned: 'left' },
    { headerName: 'Product', field: 'product_name', minWidth: 200, valueFormatter: (p) => dash(p.value) },
    qtyCol('Total', 'total_qty'),
    ...columns.map((name) =>
      qtyCol(name, undefined, {
        blankZero: true,
        colDef: { colId: `${valuesKey}:${name}`, valueGetter: (p) => p.data[valuesKey][name] || 0 },
      }),
    ),
    ...(hasOthers
      ? [
          qtyCol('Others', undefined, {
            blankZero: true,
            colDef: {
              colId: 'others',
              valueGetter: (p) =>
                p.data.total_qty - columns.reduce((total, name) => total + (p.data[valuesKey][name] || 0), 0),
            },
          }),
        ]
      : []),
  ];
  const sharedCount = useMemo(() => (data?.items || []).filter((item) => item[countKey] > 1).length, [data, countKey]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const title = (label) => [`${auditName} — ${label}`];
      const matrix = (list, key, label) => ({
        name: label,
        title: title(`Quantity per EAN by ${label.toLowerCase()}`),
        columns: [
          { label: 'EAN', key: 'ean', type: 'text', width: 16 },
          { label: 'Product', key: 'product_name', type: 'text', width: 32 },
          { label: 'Total Qty', key: 'total_qty', type: 'qty' },
          ...list.map((name) => ({ label: name, key: `${key}:${name}`, type: 'qty', value: (r) => r[key][name] ?? null })),
        ],
        rows: data.items,
      });
      await exportWorkbook(`${safeFileName(auditName)} - team and day ${todayStamp()}.xlsx`, [
        {
          name: 'People',
          title: title('Quantity by scanning person'),
          columns: [
            { label: 'Person', key: 'name', type: 'text', width: 24 },
            { label: 'Quantity', key: 'qty', type: 'qty' },
            { label: 'Items (EANs)', key: 'items', type: 'number' },
            { label: 'Scans', key: 'scans', type: 'number' },
          ],
          rows: data.people,
        },
        {
          name: 'Days',
          title: title(`Quantity by day (${data.time_zone})`),
          columns: [
            { label: 'Day', key: 'day', type: 'text', width: 14 },
            { label: 'Quantity', key: 'qty', type: 'qty' },
            { label: 'Items (EANs)', key: 'items', type: 'number' },
            { label: 'Scans', key: 'scans', type: 'number' },
          ],
          rows: data.days,
        },
        matrix(data.people.map((p) => p.name), 'by_person', 'By person'),
        matrix(data.days.map((d) => d.day), 'by_day', 'By day'),
        ...(await loadScanSheets(auditId, auditName)),
      ]);
    } finally {
      setExporting(false);
    }
  };

  if (!data?.items?.length) {
    return (
      <>
        <p className="compare-empty">No valid scans have been synced for this schedule yet.</p>
        <InvalidNotice invalid={data?.invalid} onShowScans={onShowScans} />
      </>
    );
  }

  return (
    <>
      <InvalidNotice invalid={data?.invalid} onShowScans={onShowScans} />
      <div className="compare-segmented" role="group" aria-label="Compare by">
        <button type="button" aria-pressed={by === 'person'} onClick={() => setBy('person')}>
          By person ({(data.people || []).length})
        </button>
        <button type="button" aria-pressed={by === 'day'} onClick={() => setBy('day')}>
          By day ({(data.days || []).length})
        </button>
      </div>

      <DataGrid
        key={`groups-${by}`}
        rowData={groups}
        columnDefs={groupColumns}
        getRowId={(row) => String(row[groupKey])}
        search={false}
        pageSize={20}
      />

      <DataGrid
        key={`matrix-${by}`}
        rowData={items}
        columnDefs={matrixColumns}
        getRowId={(row) => row.ean}
        pageSize={50}
        searchPlaceholder="Search EAN or product…"
        emptyMessage="Nothing to show."
        gridProps={{
          getRowClass: (p) => (p.data && p.data[countKey] > 1 ? 'data-grid__row--warn' : ''),
        }}
        toolbar={
          <Toolbar onExport={exportExcel} exporting={exporting}>
            <label className="compare-check">
              <input type="checkbox" checked={onlyShared} onChange={(event) => setOnlyShared(event.target.checked)} />
              Only EANs counted by more than one {by === 'person' ? 'person' : 'day'} ({fmtQty(sharedCount)})
            </label>
          </Toolbar>
        }
      />
      {hasOthers ? (
        <p className="compare-muted">
          Showing the {MAX_MATRIX_COLUMNS} largest {by === 'person' ? 'people' : 'days'} as columns; the Excel download has
          all of them.
        </p>
      ) : null}
    </>
  );
}

// ---- Valid & invalid scans (phone report style) ---------------------------------

const SCAN_FILTERS = [
  { id: 'invalid', label: 'Invalid (not resolved)', test: (r) => scanStatus(r) === 'invalid' },
  { id: 'resolved', label: 'Invalid resolved', test: (r) => scanStatus(r) === 'resolved' },
  { id: 'valid', label: 'Valid scans (incl. resolved)', test: (r) => r.is_valid },
  { id: 'all', label: 'All scans', test: () => true },
];

function ScansComparison({ auditName, data }) {
  const [filter, setFilter] = useState('invalid');
  const [exporting, setExporting] = useBusyLoader('Preparing Excel file…');
  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const shown = useMemo(() => {
    const test = (SCAN_FILTERS.find((f) => f.id === filter) || SCAN_FILTERS[0]).test;
    // Newest first on screen.
    return rows.filter(test).slice().reverse();
  }, [rows, filter]);
  const scanColumns = useMemo(
    () => [
      {
        headerName: 'Scanned at',
        field: 'scanned_at',
        minWidth: 180,
        filter: 'agDateColumnFilter',
        valueGetter: (p) => (p.data.scanned_at ? new Date(p.data.scanned_at) : null),
        valueFormatter: (p) => (p.value ? p.value.toLocaleString() : ''),
        getQuickFilterText: (p) => (p.value ? p.value.toLocaleString() : ''),
      },
      { headerName: 'Location', field: 'location', valueFormatter: (p) => dash(p.value) },
      {
        headerName: 'EAN',
        field: 'ean',
        minWidth: 170,
        getQuickFilterText: (p) => `${p.data.ean} ${p.data.original_ean || ''} ${p.data.scanned_code || ''}`,
        cellRenderer: (p) => (
          <>
            {p.value}
            {p.data.scanned_code && p.data.scanned_code !== p.data.ean ? (
              <small className="compare-muted"> · scanned as article {p.data.scanned_code}</small>
            ) : p.data.by_article ? (
              <ArticleTag />
            ) : null}
          </>
        ),
      },
      { headerName: 'Description', field: 'product_name', minWidth: 200, valueFormatter: (p) => dash(p.value) },
      { headerName: 'Username', field: 'scanned_by' },
      qtyCol('Scan qty', 'qty'),
      {
        headerName: 'Status',
        colId: 'status',
        minWidth: 220,
        autoHeight: true,
        valueGetter: (p) => SCAN_STATUS_LABEL[scanStatus(p.data)],
        getQuickFilterText: (p) => `${SCAN_STATUS_LABEL[scanStatus(p.data)]} ${p.data.corrected_by_name || ''}`,
        cellRenderer: (p) => <ScanStatusPill row={p.data} />,
      },
      { headerName: 'Device', field: 'device_id', valueFormatter: (p) => dash(p.value) },
    ],
    [],
  );

  const exportExcel = async () => {
    setExporting(true);
    try {
      await exportWorkbook(`${safeFileName(auditName)} - valid and invalid scans ${todayStamp()}.xlsx`, [
        {
          name: 'Summary',
          title: [`${auditName} — Valid and invalid scans by person`],
          notes: [`Generated ${new Date().toLocaleString()}. Total = valid + invalid.`],
          columns: [
            { label: 'Username', key: 'name', type: 'text', width: 22 },
            { label: 'Valid Scans', key: 'valid_scans', type: 'number' },
            { label: 'Valid Qty', key: 'valid_qty', type: 'qty' },
            { label: 'Invalid Scans', key: 'invalid_scans', type: 'number' },
            { label: 'Invalid Qty', key: 'invalid_qty', type: 'qty' },
            { label: 'Total Qty', key: 'total', type: 'qty', value: (r) => r.valid_qty + r.invalid_qty },
          ],
          rows: [
            ...(data.people || []),
            {
              name: 'TOTAL',
              valid_scans: summary.valid_scans,
              valid_qty: summary.valid_qty,
              invalid_scans: summary.invalid_scans,
              invalid_qty: summary.invalid_qty,
            },
          ],
        },
        ...scanSheets(auditName, data),
      ]);
    } finally {
      setExporting(false);
    }
  };

  if (!rows.length) {
    return <p className="compare-empty">No scans have been synced for this schedule yet.</p>;
  }

  return (
    <>
      <section className="compare-cards">
        <Card
          label="Valid scans"
          value={fmtQty(summary.valid_scans)}
          note={`Qty ${fmtQty(summary.valid_qty)} · ${fmtQty(summary.valid_eans)} EANs`}
          tone="good"
        />
        <Card
          label="Invalid scans"
          value={fmtQty(summary.invalid_scans)}
          note={`Qty ${fmtQty(summary.invalid_qty)} · ${fmtQty(summary.invalid_eans)} EANs not in the product list`}
          tone="bad"
        />
        <Card
          label="Invalid resolved"
          value={fmtQty(summary.resolved_scans || 0)}
          note={`Qty ${fmtQty(summary.resolved_qty || 0)} · corrected by the store team, now valid`}
          tone="good"
        />
        <Card
          label="Total scanned"
          value={fmtQty((summary.valid_qty || 0) + (summary.invalid_qty || 0))}
          note={`${fmtQty((summary.valid_scans || 0) + (summary.invalid_scans || 0))} scans · valid + invalid`}
        />
      </section>

      <DataGrid
        rowData={data.people || []}
        columnDefs={[
          { headerName: 'Username', field: 'name', minWidth: 180 },
          qtyCol('Valid scans', 'valid_scans'),
          qtyCol('Valid qty', 'valid_qty'),
          qtyCol('Invalid scans', 'invalid_scans'),
          {
            ...qtyCol('Invalid qty', 'invalid_qty'),
            cellClass: (p) => (p.value ? 'data-grid__neg' : ''),
          },
          qtyCol('Total qty', undefined, {
            colDef: { colId: 'total', valueGetter: (p) => p.data.valid_qty + p.data.invalid_qty },
          }),
        ]}
        getRowId={(row) => row.name}
        search={false}
        pageSize={20}
      />

      <DataGrid
        rowData={shown}
        columnDefs={scanColumns}
        getRowId={null}
        pageSize={50}
        searchPlaceholder="Search EAN, product, person, device or location…"
        emptyMessage="Nothing to show for this filter."
        toolbar={
          <Toolbar
            filter={filter}
            setFilter={setFilter}
            filters={SCAN_FILTERS}
            onExport={exportExcel}
            exporting={exporting}
          />
        }
      />
      <p className="compare-muted">{SCAN_LOCATION_NOTE}</p>
    </>
  );
}

// ---- Panel --------------------------------------------------------------------

/**
 * Comparison tab of the audit portal. Data is loaded per mode when the tab
 * is opened and on "Refresh" (not polled, as it can be large).
 */
function ComparisonPanel({ auditId, auditName, canManage }) {
  const [mode, setMode] = useState('book');
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(
    async (target) => {
      setLoading(true);
      setError('');
      try {
        const data = await auditService.getComparison(auditId, target);
        setCache((prev) => ({ ...prev, [target]: { data, at: new Date() } }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [auditId],
  );

  useEffect(() => {
    if (!cache[mode]) load(mode);
  }, [mode, cache, load]);

  const entry = cache[mode];

  return (
    <section className="audit-portal__panel compare">
      <header className="compare-header">
        <div className="compare-segmented" role="tablist" aria-label="Comparison">
          {MODES.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={mode === item.id} aria-pressed={mode === item.id} onClick={() => setMode(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
        <div className="compare-header__right">
          {entry ? <span className="compare-muted">Updated {entry.at.toLocaleTimeString()}</span> : null}
          <button type="button" className="compare-button compare-button--ghost" onClick={() => load(mode)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error ? <p className="compare-error">{error}</p> : null}

      {!entry ? (
        loading ? <PageLoader label="Loading comparison…" /> : null
      ) : mode === 'book' ? (
        <BookComparison
          auditId={auditId}
          auditName={auditName}
          data={entry.data}
          canManage={canManage}
          onChanged={() => load('book')}
          onShowScans={() => setMode('scans')}
        />
      ) : mode === 'master' ? (
        <MasterComparison auditId={auditId} auditName={auditName} data={entry.data} onShowScans={() => setMode('scans')} />
      ) : mode === 'people' ? (
        <PeopleComparison auditId={auditId} auditName={auditName} data={entry.data} onShowScans={() => setMode('scans')} />
      ) : (
        <ScansComparison auditName={auditName} data={entry.data} />
      )}
    </section>
  );
}

export default ComparisonPanel;
